import { useCallback, useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.scss";
import {
  DeviceFillIcon,
  PersonIcon,
  UserFillIcon,
} from "../../../assets/icons-components/Icons";
import { GetAdminDashboardData } from "../../../utils/apiCalls/dashboard";
import DashboardCard from "../../../components/DashboardCard/DashboardCard";
import Buttons from "../../../components/Buttons/Buttons";
import { UserContext } from "../../../store/UserContext";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import WatchIcon from "@mui/icons-material/Watch";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import AndroidIcon from "@mui/icons-material/Android";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import useLatestAppVersions from "../../../hooks/useLatestAppVersions";
import { APP_PLATFORM } from "../../../utils/appPlatform";

// Never render undefined/null while data is loading or a count is missing.
const n = (value) => (typeof value === "number" ? value : 0);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { loginUserDetails } = useContext(UserContext);
  const admin = loginUserDetails?.currentSuperAdminModel || {};

  const [dashboardData, setDashboardData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const { latestVersions } = useLatestAppVersions();

  const fetchAdminDashboardData = useCallback(async () => {
    setIsLoading(true);
    setFetchError("");
    try {
      const res = await GetAdminDashboardData();
      const { data, message, statusCode } = res;
      if (statusCode === 200) {
        setDashboardData(data || {});
      } else {
        setFetchError(message || "Failed to load dashboard data");
      }
    } catch (error) {
      setFetchError(error?.message || "Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminDashboardData();
  }, [fetchAdminDashboardData]);

  const assigned = n(dashboardData.numberOfAssignedDevice);
  const totalDev = n(dashboardData.numberOfDeviceInInventory);
  const available = n(dashboardData.numberOfUnAssignedDevice);

  return (
    <div className="hp-dashboard admin-dashboard">
      {/* CLEAN PAGE HEADER */}
      <div className="hp-dashboard_header">
        <div className="hp-dashboard_header_info">
          <h1>Welcome back, {admin.name ? admin.name : "Administrator"}</h1>
          <p>
            Platform overview of healthcare professionals, device inventory, and system logs.
          </p>
        </div>
        <div className="hp-dashboard_header_actions">
          <Buttons
            text="Add Healthcare Professional"
            type="button"
            variant="primary"
            fullWidth={false}
            style={{ height: 38 }}
            icon={<MedicalServicesIcon fontSize="small" />}
            onClick={() => navigate("/hpform")}
          />
          <Buttons
            text="Add Device"
            type="button"
            variant="secondary"
            fullWidth={false}
            style={{ height: 38 }}
            icon={<WatchIcon fontSize="small" />}
            onClick={() => navigate("/add-device")}
          />
        </div>
      </div>

      {fetchError && (
        <div className="hp-dashboard_error">
          <span>{fetchError}</span>
          <Buttons
            text="Retry"
            type="button"
            variant="smallsecondary"
            onClick={fetchAdminDashboardData}
          />
        </div>
      )}

      {/* METRICS ROW */}
      <div className="hp-dashboard_cards">
        <DashboardCard
          icon={<UserFillIcon />}
          isLoading={isLoading}
          value={n(dashboardData.numberOfActiveHealthProfessional)}
          label="Active Healthcare Professionals"
          onClick={() => navigate("/hptable")}
        />
        <DashboardCard
          icon={<PersonIcon />}
          isLoading={isLoading}
          value={n(dashboardData.numberOfTotalPatient)}
          label="Total Patients"
          onClick={() => navigate("/hptable")}
        />
        <DashboardCard
          icon={<DeviceFillIcon />}
          isLoading={isLoading}
          value={`${assigned} / ${totalDev}`}
          label="Assigned Devices (of Inventory)"
          onClick={() => navigate("/add-device")}
        />
        <DashboardCard
          icon={<DeviceFillIcon />}
          isLoading={isLoading}
          value={`${available} / ${totalDev}`}
          label="Available Devices (of Inventory)"
          onClick={() => navigate("/add-device")}
        />
      </div>

      {/* DEVICE INVENTORY OVERVIEW */}
      <div className="hp-dashboard_grid">
        <div className="hp-dashboard_grid_main">
          <div className="hp-card">
            <div className="hp-card_header">
              <div className="hp-card_header_title">
                <WatchIcon className="hp-card_icon" />
                <div>
                  <h3>Device Inventory & Allocation Status</h3>
                  <p>Global hardware sensor deployment metrics</p>
                </div>
              </div>
            </div>

            <div className="hp-card_body">
              <div className="hp-inventory-status">
                <div className="hp-inventory-status_top">
                  <div>
                    <span className="hp-inventory-status_pct">
                      {totalDev > 0 ? Math.round((assigned / totalDev) * 100) : 0}%
                    </span>
                    <span className="hp-inventory-status_label">Global Allocation Rate</span>
                  </div>
                  <span className="hp-inventory-status_count">
                    {assigned} / {totalDev} Deployed
                  </span>
                </div>

                <div className="hp-inventory-legend">
                  <div className="hp-inventory-legend_item">
                    <strong>{assigned}</strong>
                    <span>Assigned</span>
                  </div>
                  <div className="hp-inventory-legend_item">
                    <strong>{available}</strong>
                    <span>Available</span>
                  </div>
                  <div className="hp-inventory-legend_item">
                    <strong>{totalDev}</strong>
                    <span>Total</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* APP VERSIONS */}
        <div className="hp-dashboard_grid_side">
          <div className="hp-card">
            <div className="hp-card_header">
              <div className="hp-card_header_title">
                <SystemUpdateAltIcon className="hp-card_icon" />
                <div>
                  <h3>App Versions</h3>
                  <p>Latest published builds</p>
                </div>
              </div>
              <button
                className="hp-card_header_link"
                onClick={() => navigate("/app-versions")}
              >
                Manage <ArrowForwardIcon fontSize="small" />
              </button>
            </div>

            <div className="hp-card_body">
              <div className="app-version-widget">
                <div className="app-version-widget_row">
                  <span className="app-version-widget_platform">
                    <AndroidIcon sx={{ color: "#1e7e34", fontSize: 20 }} />
                    Android
                  </span>
                  <span className="app-version-widget_value">
                    {latestVersions?.[APP_PLATFORM.ANDROID]?.version
                      ? `v${latestVersions[APP_PLATFORM.ANDROID].version}`
                      : "—"}
                  </span>
                </div>
                <div className="app-version-widget_row">
                  <span className="app-version-widget_platform">
                    <PhoneIphoneIcon sx={{ color: "#104581", fontSize: 20 }} />
                    iOS
                  </span>
                  <span className="app-version-widget_value">
                    {latestVersions?.[APP_PLATFORM.IOS]?.version
                      ? `v${latestVersions[APP_PLATFORM.IOS].version}`
                      : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

